import { useState } from 'react';
import { useNavigate } from 'react-router';
import { isSealed } from '@orbit-mind/domain';
import { MAX_BACKUP_BYTES, readPortableBackup, type PortableBackup } from '@orbit-mind/api-client';
import { useLocale } from '@orbit-mind/engine-react';
import { backupHash, restoreBackup, restoreLibraryBackup } from '../engine/backup';
import { localStorageLayer } from '../engine/storage';
import { Button } from '../ui/controls';

export function BackupImport(): React.JSX.Element {
  const [layer] = useState(localStorageLayer);
  const [preview, setPreview] = useState<PortableBackup | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { text: t } = useLocale();
  const maps =
    preview === null ? [] : preview.kind === 'map' ? [preview.value] : preview.value.maps;
  return (
    <section className="border-edge space-y-3 rounded-xl border p-3">
      <label>
        {t(
          'Restore backup with attachments (offline, up to 100 MB)',
          'Restaurar backup com anexos (offline, até 100 MB)',
        )}
        <input
          type="file"
          accept=".orbitbackup,application/json"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            setError('');
            setPreview(null);
            if (file.size > MAX_BACKUP_BYTES) {
              setError(t('Limit: 100 MB.', 'Limite: 100 MB.'));
              return;
            }
            setBusy(true);
            void file
              .text()
              .then((text) => readPortableBackup(text, backupHash))
              .then(setPreview)
              .catch((cause: unknown) => {
                setError(cause instanceof Error ? cause.message : 'Backup inválido.');
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        />
      </label>
      {busy && <p role="status">{t('Checking and saving…', 'Verificando e salvando…')}</p>}
      {error && <p role="alert">{error}</p>}
      {preview && (
        <div>
          <strong>
            {preview.kind === 'map'
              ? preview.value.document.map.title
              : t('Library backup', 'Backup da biblioteca')}
          </strong>
          <p>
            {maps.some((map) => isSealed(map.document))
              ? t(
                  'Protected; your password is still required.',
                  'Protegido; a senha continua necessária.',
                )
              : `${String(maps.reduce((n, map) => n + Object.keys(map.document.nodes).length, 0))} ${t('topics', 'tópicos')}`}{' '}
            · {maps.length} {t('maps', 'mapas')} ·{' '}
            {maps.reduce((n, map) => n + map.files.length, 0)} {t('files', 'arquivos')}
          </p>
          {preview.kind === 'library' && (
            <p>
              {preview.value.organization.folders.length}{' '}
              {t(
                'folders; tags, favorites and presentations preserved.',
                'pastas; tags, favoritos e apresentações preservados.',
              )}
            </p>
          )}
          <p>
            {t(
              'Creates an independent copy. Existing maps are preserved.',
              'Cria uma cópia independente. Os mapas existentes são preservados.',
            )}
          </p>
          <Button
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError('');
              void (
                preview.kind === 'map'
                  ? restoreBackup(layer, preview.value)
                  : restoreLibraryBackup(layer, preview.value).then(() => null)
              )
                .then((id) => {
                  if (id === null) {
                    void navigate('/');
                    window.location.reload();
                  } else void navigate(`/map/${id}`);
                })
                .catch((cause: unknown) => {
                  setError(cause instanceof Error ? cause.message : 'Falha ao restaurar.');
                })
                .finally(() => {
                  setBusy(false);
                });
            }}
          >
            {t('Restore copy', 'Restaurar cópia')}
          </Button>
        </div>
      )}
    </section>
  );
}
